<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

import NooviConnectAPI from 'dashboard/api/nooviConnect';
import Button from 'dashboard/components-next/button/Button.vue';
import { useAlert } from 'dashboard/composables';

// Standalone C1 widget: shown for an inbound WhatsApp call activity (content_attributes.call).
// Lets the agent reject the call. Answering with audio (C2) is a future phase (needs WAHA PR #2128).
const props = defineProps({
  inboxId: { type: [String, Number], required: true },
  call: { type: Object, required: true }, // { id, from, isVideo } (camelCased by MessageList)
});

const { t } = useI18n();
const route = useRoute();
const accountId = computed(() => route.params.accountId);
const rejecting = ref(false);
const rejected = ref(false);

const phone = computed(() => {
  const from = props.call?.from?.toString() || '';
  return from.split('@')[0] || from;
});

// MessageList deep-camelCases the message, so content_attributes.call.is_video
// arrives as isVideo. Accept both shapes so the video/voice label is correct.
const isVideoCall = computed(
  () => props.call?.isVideo ?? props.call?.is_video ?? false
);

const label = computed(() =>
  isVideoCall.value
    ? t('INBOX_MGMT.ADD.WHATSAPP.NOOVI_CONNECT.CALL.VIDEO_RECEIVED', {
        phone: phone.value,
      })
    : t('INBOX_MGMT.ADD.WHATSAPP.NOOVI_CONNECT.CALL.VOICE_RECEIVED', {
        phone: phone.value,
      })
);

const reject = async () => {
  if (rejecting.value || rejected.value || !props.call?.id) return;
  rejecting.value = true;
  try {
    await NooviConnectAPI.rejectCall(
      accountId.value,
      props.inboxId,
      props.call.id,
      props.call.from
    );
    rejected.value = true;
  } catch {
    useAlert(t('INBOX_MGMT.ADD.WHATSAPP.NOOVI_CONNECT.CALL.REJECT_ERROR'));
  } finally {
    rejecting.value = false;
  }
};
</script>

<template>
  <div
    class="flex items-center justify-center gap-2 rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm"
  >
    <span class="text-n-slate-12">{{ label }}</span>
    <span v-if="rejected" class="font-medium text-n-slate-11">
      {{ t('INBOX_MGMT.ADD.WHATSAPP.NOOVI_CONNECT.CALL.REJECTED') }}
    </span>
    <Button
      v-else
      size="sm"
      color="ruby"
      :is-loading="rejecting"
      :label="t('INBOX_MGMT.ADD.WHATSAPP.NOOVI_CONNECT.CALL.REJECT')"
      @click="reject"
    />
  </div>
</template>
